import { useState, useRef, useCallback, useEffect } from "react";
import ImageCropperModal from "./ImageCropperModal";
import {
	classifyUploadError,
	resizeImageToBlob,
	uploadBlob,
} from "../../lib/uploadImage";

interface ImageUploaderProps {
	value: string;
	onChange: (url: string) => void;
	label?: string;
	folder?: string;
	className?: string;
	crop?: boolean; // true = abre cropper interactivo antes de subir
	maxSizeMB?: number;
	keepGif?: boolean; // true = los GIF se suben tal cual para no perder la animación
	helper?: React.ReactNode; // indicaciones extra debajo del campo (p. ej. medidas del arte)
	[key: string]: unknown;
}

export default function ImageUploader({
	value,
	onChange,
	label = "Imagen",
	folder = "general",
	className = "",
	crop = true,
	maxSizeMB = 15,
	keepGif = false,
	helper,
}: ImageUploaderProps) {
	const [uploading, setUploading] = useState(false);
	const [dragActive, setDragActive] = useState(false);
	const [error, setError] = useState("");
	const [progress, setProgress] = useState(0);
	const [cropperOpen, setCropperOpen] = useState(false);
	const [cropImageUrl, setCropImageUrl] = useState("");
	const [pendingFile, setPendingFile] = useState<File | null>(null);
	const [showChoiceModal, setShowChoiceModal] = useState(false);
	const [choiceImageUrl, setChoiceImageUrl] = useState("");
	const [cropRatio, setCropRatio] = useState(16 / 9);
	const inputRef = useRef<HTMLInputElement>(null);
	// Always call the latest onChange without stale closures
	const onChangeRef = useRef(onChange);
	useEffect(() => {
		onChangeRef.current = onChange;
	}, [onChange]);

	const doUpload = async (blob: Blob, ext?: string) => {
		const imageUrl = await uploadBlob(blob, folder, setProgress, ext);
		onChangeRef.current(imageUrl);
	};

	const uploadOriginalGif = async (file: File) => {
		setError("");
		setUploading(true);
		setProgress(30);
		try {
			await doUpload(file, "gif");
		} catch (err) {
			const rawMsg = err instanceof Error ? err.message : "Error al subir la imagen.";
			setError(classifyUploadError(rawMsg));
		} finally {
			setUploading(false);
			setTimeout(() => setProgress(0), 500);
		}
	};

	const processFullImage = async (file: File) => {
		setShowChoiceModal(false);
		setError("");
		setUploading(true);
		setProgress(0);
		try {
			setProgress(10);
			const resizedBlob = await resizeImageToBlob(file);
			setProgress(30);
			await doUpload(resizedBlob);
		} catch (err) {
			const rawMsg = err instanceof Error ? err.message : "Error al subir la imagen.";
			setError(classifyUploadError(rawMsg));
		} finally {
			setUploading(false);
			setTimeout(() => setProgress(0), 500);
			if (choiceImageUrl) URL.revokeObjectURL(choiceImageUrl);
			setChoiceImageUrl("");
		}
	};

	const openCropperForFile = (file: File) => {
		setShowChoiceModal(false);
		const url = URL.createObjectURL(file);
		// Detect natural aspect ratio to show full image in frame by default
		const img = new Image();
		img.onload = () => {
			const ratio = img.naturalWidth / img.naturalHeight;
			setCropRatio(Number.isFinite(ratio) && ratio > 0 ? ratio : 16 / 9);
			setCropImageUrl(url);
			setPendingFile(file);
			setCropperOpen(true);
		};
		img.onerror = () => {
			setCropRatio(16 / 9);
			setCropImageUrl(url);
			setPendingFile(file);
			setCropperOpen(true);
		};
		img.src = url;
	};

	const resizeAndUpload = async (file: File) => {
		if (keepGif && file.type === "image/gif") {
			await uploadOriginalGif(file);
			return;
		}
		if (crop) {
			const url = URL.createObjectURL(file);
			setChoiceImageUrl(url);
			setPendingFile(file);
			setShowChoiceModal(true);
			return;
		}
		// No crop: process full image directly
		await processFullImage(file);
	};

	const handleCropComplete = async (croppedBlob: Blob) => {
		setCropperOpen(false);
		if (cropImageUrl) URL.revokeObjectURL(cropImageUrl);
		setCropImageUrl("");
		setPendingFile(null);

		setUploading(true);
		setProgress(50);
		try {
			await doUpload(croppedBlob);
		} catch (err) {
			const rawMsg =
				err instanceof Error
					? err.message
					: "Error al subir la imagen. Intenta de nuevo o contacta soporte.";
			setError(classifyUploadError(rawMsg));
		} finally {
			setUploading(false);
			setTimeout(() => setProgress(0), 500);
		}
	};

	const handleCropCancel = () => {
		setCropperOpen(false);
		if (cropImageUrl) URL.revokeObjectURL(cropImageUrl);
		setCropImageUrl("");
		setPendingFile(null);
	};

	const handleDrag = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.type === "dragenter" || e.type === "dragover") {
			setDragActive(true);
		} else if (e.type === "dragleave") {
			setDragActive(false);
		}
	}, []);

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);
		if (e.dataTransfer.files && e.dataTransfer.files[0]) {
			const file = e.dataTransfer.files[0];
			if (!file.type.startsWith("image/")) {
				setError("Solo se permiten archivos de imagen (JPG, PNG, WebP, GIF).");
				return;
			}
			if (file.size > maxSizeMB * 1024 * 1024) {
				setError(`El archivo es muy grande. Maximo ${maxSizeMB} MB.`);
				return;
			}
			resizeAndUpload(file);
		}
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files[0]) {
			const file = e.target.files[0];
			if (!file.type.startsWith("image/")) {
				setError("Solo se permiten archivos de imagen.");
				return;
			}
			if (file.size > maxSizeMB * 1024 * 1024) {
				setError(`El archivo es muy grande. Maximo ${maxSizeMB} MB.`);
				return;
			}
			resizeAndUpload(file);
		}
		// Reset input so the same file can be selected again if needed
		if (inputRef.current) {
			inputRef.current.value = "";
		}
	};

	const handleRemove = useCallback(() => {
		onChangeRef.current("");
		setError("");
	}, []);

	return (
		<div className={className}>
			{label && (
				<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
					{label} *
				</label>
			)}

			{value ? (
				<div className="space-y-3">
					<div className="relative rounded-xl overflow-hidden border border-slate-200 bg-slate-100 aspect-video">
						<img
							src={value}
							alt="Preview"
							className="w-full h-full object-contain"
						/>
						<button
							type="button"
							onClick={handleRemove}
							className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-white/90 hover:bg-white text-red-500 rounded-lg shadow-sm transition-colors cursor-pointer"
							title="Eliminar imagen"
						>
							<i className="ri-close-line text-sm" />
						</button>
					</div>

					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={() => inputRef.current?.click()}
							className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
						>
							<i className="ri-refresh-line" /> Cambiar imagen
						</button>
					</div>
				</div>
			) : (
				<div
					onDragEnter={handleDrag}
					onDragLeave={handleDrag}
					onDragOver={handleDrag}
					onDrop={handleDrop}
					onClick={() => inputRef.current?.click()}
					className={`relative rounded-xl border-2 border-dashed transition-all cursor-pointer p-6 text-center
            ${
							dragActive
								? "border-[#0B1F3A] bg-[#0B1F3A]/5"
								: "border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-slate-100"
						}
          `}
				>
					{uploading ? (
						<div className="flex flex-col items-center gap-2">
							<i className="ri-loader-4-line animate-spin text-[#0B1F3A] text-2xl" />
							<p className="text-slate-500 text-xs font-medium">
								{progress < 30
									? "Optimizando imagen..."
									: progress < 60
										? "Preparando subida..."
										: progress < 90
											? "Subiendo al servidor..."
											: "Finalizando..."}
							</p>
							{/* Progress bar */}
							<div className="w-full max-w-[200px] h-1.5 bg-slate-200 rounded-full overflow-hidden">
								<div
									className="h-full bg-[#0B1F3A] rounded-full transition-all duration-300"
									style={{ width: `${progress}%` }}
								/>
							</div>
						</div>
					) : (
						<div className="flex flex-col items-center gap-2">
							<div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl border border-slate-200">
								<i className="ri-upload-cloud-2-line text-slate-400 text-xl" />
							</div>
							<div>
								<p className="text-[#0B1F3A] text-sm font-semibold">
									Arrastra una imagen aqui o haz clic para subir
								</p>
								<p className="text-slate-500 text-xs mt-1 font-medium">
									Puedes subir la imagen completa o recortarla antes de subir.
								</p>
								<p className="text-slate-400 text-[10px] mt-0.5">
									JPG, PNG, WebP, GIF · Max. {maxSizeMB} MB
								</p>
							</div>
						</div>
					)}

					<input
						ref={inputRef}
						type="file"
						accept="image/jpeg,image/png,image/webp,image/gif"
						onChange={handleInputChange}
						className="hidden"
					/>
				</div>
			)}

			{error && (
				<div className="flex items-start gap-2 mt-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
					<i className="ri-error-warning-line text-red-500 text-xs mt-0.5 flex-shrink-0" />
					<p className="text-red-600 text-xs leading-relaxed">{error}</p>
				</div>
			)}

			{value && (
				<p className="text-slate-400 text-[10px] mt-1">
					URL guardada correctamente
				</p>
			)}

			{helper}

			{/* Choice Modal: Full or Crop */}
			{showChoiceModal && choiceImageUrl && (
				<div
					className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
					onClick={(e) => {
						if (e.target === e.currentTarget) {
							setShowChoiceModal(false);
							if (choiceImageUrl) URL.revokeObjectURL(choiceImageUrl);
							setChoiceImageUrl("");
							setPendingFile(null);
						}
					}}
				>
					<div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl">
						<div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
							<h3
								className="text-[#0B1F3A] font-bold text-sm"
								style={{ fontFamily: "'Sora', sans-serif" }}
							>
								<i className="ri-image-line mr-1.5" />
								¿Cómo quieres subir esta imagen?
							</h3>
							<button
								onClick={() => {
									setShowChoiceModal(false);
									if (choiceImageUrl) URL.revokeObjectURL(choiceImageUrl);
									setChoiceImageUrl("");
									setPendingFile(null);
								}}
								className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 transition-colors cursor-pointer flex-shrink-0"
							>
								<i className="ri-close-line" />
							</button>
						</div>

						<div className="p-5 flex flex-col sm:flex-row gap-4">
							{/* Preview */}
							<div className="flex-1 rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
								<img
									src={choiceImageUrl}
									alt="Preview"
									className="w-full h-48 object-contain object-center"
								/>
							</div>

							{/* Options */}
							<div className="flex-1 flex flex-col gap-3 justify-center">
								<button
									type="button"
									onClick={() => {
										if (pendingFile) processFullImage(pendingFile);
									}}
									className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:border-[#0B1F3A] hover:bg-slate-50 transition-all text-left cursor-pointer group"
								>
									<div className="w-10 h-10 flex items-center justify-center bg-emerald-50 rounded-lg group-hover:bg-emerald-100 transition-colors flex-shrink-0">
										<i className="ri-image-line text-emerald-600 text-lg" />
									</div>
									<div>
										<p className="text-[#0B1F3A] font-bold text-sm">
											Subir imagen completa
										</p>
										<p className="text-slate-400 text-xs mt-0.5">
											Se redimensiona manteniendo su proporción original. Ideal para fotos de personas, eventos o cuando quieres que salga todo.
										</p>
									</div>
								</button>

								<button
									type="button"
									onClick={() => {
										if (pendingFile) openCropperForFile(pendingFile);
									}}
									className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:border-[#0B1F3A] hover:bg-slate-50 transition-all text-left cursor-pointer group"
								>
									<div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-lg group-hover:bg-amber-100 transition-colors flex-shrink-0">
										<i className="ri-crop-line text-amber-600 text-lg" />
									</div>
									<div>
										<p className="text-[#0B1F3A] font-bold text-sm">
											Recortar y ajustar
										</p>
										<p className="text-slate-400 text-xs mt-0.5">
											Abre el editor para ajustar zoom, mover y recortar la foto. Ideal para banners y vistas previas de artículos.
										</p>
									</div>
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Interactive Cropper Modal */}
			{cropperOpen && cropImageUrl && (
				<ImageCropperModal
					imageUrl={cropImageUrl}
					targetRatio={cropRatio}
					onCrop={handleCropComplete}
					onCancel={handleCropCancel}
				/>
			)}
		</div>
	);
}